/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */

import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { groupBy } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { viewPortHeight_355 } from '../../../../SharedModules/CommonConstants/commonConstants';
import TableGridWithFeatures from '../../../../SharedModules/Table/TableGridWithFeatures';
import CustomSeparator from '../../../../SharedModules/Breadcrumbs/BreadcrumbsComponent';
import {
  getQuarterlyPayment,
  getInvoiceNumChecked,
  setExportAllData,
  getQuarterlyPaymentForExportAll,
  setLoading,
  setPaginationIndex,
} from '../Store/actions';
import PaginationBox from './PaginationBox';
import Spinner from '../../../../SharedModules/Spinner/Spinner';
import { TableHeaderForDetails, ndcTableHeads } from './tableHeads';
import { exportAllPDF } from '../../../../SharedModules/pdfExport/exportAll';
import { clearReduxForFilterAndSort } from '../../../../SharedModules/Table/store/Actions';

const DisplayQuarterlyPayments = () => {
  const dispatch = useDispatch();

  const {
    isLoading,
    threshold,
    invNumCheckedResult,
    exportAllData,

    displayObj: {
      rebateProgram,
      selectedRows,
      reportQuarter,
      useBalanceThreshold,
      searchResult,
      dramsInvoice,
      earliestQtr,
      rebateProgrameName,
      countValue,
    },
  } = useSelector((state) => state?.sharedState?.QuarterlyPaymentsReducer);
  const logInUser = useSelector(
    (state) => state?.sharedState?.SharedReducer?.logInUser
  );
  const rebateProgramNameValue = rebateProgrameName;
  const userDefaults = useSelector(
    (state) => state?.sharedState?.UserDefaultReducer?.UserDefaultsValues?.data
  );
  const gridRef = useRef(null);
  const [rows, setRows] = React.useState(searchResult);
  const [checkRows, setCheckRows] = React.useState(invNumCheckedResult);
  const [totalAmt, setTotalAmt] = React.useState('');
  const [count, setCount] = React.useState(countValue || 1);
  const [columnApi, setColumnApi] = React.useState(null);
  const [exportAllClicked, setExportAllClicked] = React.useState(false);
  const [paginationArray, setPaginationArray] = React.useState([
    ...Array(selectedRows.length).keys(),
  ]);
  const [numOfApi, setNumOfApi] = React.useState(1);
  const [fullScreenView, setFullScreenView] = React.useState(false);

  const [filteredRows, setFilteredRow] = React.useState([]);
  const [filterApply, setFilterApply] = React.useState(false);
  const [applyPagination, setApplyPagination] = React.useState(false);

  const getFilterApply = (filterApplied) => {
    setFilterApply(filterApplied);
  };

  const changeFullScreenView = () => {
    setFullScreenView(!fullScreenView);
  };

  // set pagination array
  React.useEffect(() => {
    if (countValue) {
      setCount(countValue || 1);
      setApplyPagination(true);
    }
  }, [countValue]);

  React.useEffect(() => {
    setPaginationArray(selectedRows);
  }, [selectedRows]);

  const handleClick = (type, value) => {
    dispatch(clearReduxForFilterAndSort('/quarterlyPaymentDisplay'));
    if (type === 'next') {
      dispatch(setPaginationIndex(count + 1));
    }

    if (type === 'previous') {
      dispatch(setPaginationIndex(count - 1));
    }

    if (type === 'change') {
      if (value < 1 || value > paginationArray.length) {
        dispatch(setPaginationIndex(paginationArray.length));
      } else {
        dispatch(setPaginationIndex(value));
      }
    }
  };

  const handleBlur = (value) => {
    dispatch(clearReduxForFilterAndSort('/quarterlyPaymentDisplay'));
    const pageNumber = parseInt(value);
    let currentPage = 1;
    if (value === '' || isNaN(pageNumber) || pageNumber < 1) {
      currentPage = 1;
      setCount(1); 
    } else if (pageNumber > paginationArray.length) {
      currentPage = paginationArray.length;
      setCount(paginationArray.length);
    } else {
      setCount(pageNumber);
      currentPage = pageNumber;
    }
    return currentPage;
  };

  // Dispatch QuartErly Payment API
  React.useEffect(() => {
    if (applyPagination) {
      if (selectedRows[count - 1]?.mfgId) {
        dispatch(
          getQuarterlyPayment(
            {
              rebateProgramCd: rebateProgram,
              quarter:
                reportQuarter === 'All'
                  ? '1Q1991'
                  : reportQuarter === 'DRAMS Invoice Start Quarter'
                  ? dramsInvoice
                  : reportQuarter === 'Earliest Quarter for Drug Details'
                  ? earliestQtr
                  : '',
              mfgIds: [selectedRows[count - 1]?.mfgId],
              thresh: useBalanceThreshold ? threshold : null,
            },
            true
          )
        );
      }
    }
  }, [count]);

  // dispatch Inv num checked API
  React.useEffect(() => {
    if (searchResult?.length > 0) {
      const invNums = searchResult.map((item) => item.invoiceNum);
      dispatch(
        getInvoiceNumChecked({
          rebateProgramCd: rebateProgram,
          quarter:
            reportQuarter === 'All'
              ? '1Q1991'
              : reportQuarter === 'DRAMS Invoice Start Quarter'
              ? dramsInvoice
              : reportQuarter === 'Earliest Quarter for Drug Details'
              ? earliestQtr
              : '',
          mfgIds: [searchResult[0]?.mfgId],
          thresh: useBalanceThreshold ? threshold : null,
          invoiceNum: invNums,
        })
      );
    }
  }, [searchResult]);

  React.useEffect(() => {
    if (searchResult?.length > 0 && invNumCheckedResult?.length > 0) {
      const tempObj = {};
      for (let i = 0; i < invNumCheckedResult?.length; i += 1) {
        if (tempObj[invNumCheckedResult[i].invoiceNum]) {
          tempObj[invNumCheckedResult[i].invoiceNum].push(
            invNumCheckedResult[i]
          );
        } else {
          tempObj[invNumCheckedResult[i].invoiceNum] = [invNumCheckedResult[i]];
        }
      }

      const searchResultData = searchResult.map((item) => {
        return {
          ...item,
          ind: tempObj[item.invoiceNum.toString()]?.length > 0 ? 1 : 0,
        };
      });

      setRows(searchResultData);
    }
  }, [searchResult, invNumCheckedResult, count]);

  // set Search result data
  React.useEffect(() => {
    if (searchResult && searchResult?.length) {
      const totalSum = searchResult.reduce((acc, ele) => {
        return (acc += ele.paidAmt);
      }, 0);
      setTotalAmt(totalSum);
      setRows(searchResult);
    } else {
      setTotalAmt(0);
      setRows([]);
    }
  }, [searchResult]);

  // dispatch drams and earliest qtr api
  // React.useEffect(() => {
  //   dispatch(
  //     getDramsInvStartQtr({
  //       rebateProgCode: rebateProgram,
  //       allRpc: 0,
  //     })
  //   );
  //   dispatch(
  //     getEarliestDrugQtr({
  //       rebateProgramCd: rebateProgram,
  //     })
  //   );
  // }, []);

  const sum = (property) => {
    // const displayedRows = rows;
    const displayedRows = filterApply ? filteredRows : rows;

    const data = displayedRows?.reduce((accumulator, object) => {
      return (
        accumulator + (object[property] ? parseFloat(object[property]) : null)
      );
    }, 0);
    return data ? parseFloat(data.toFixed(2)) : '0.00';
  };
  const createData = (count, prefix) => {
    const result = [];
    for (let i = 0; i < count; i += 1) {
      result.push({
        isBottomRow: true,
        sumOfAmount: sum('sumOfAmount'),
        totCurrQtrAmt: sum('totCurrQtrAmt'),
        paidAmt: sum('paidAmt'),
        qtr: 'Total: ',
        ind: '',
      });
    }
    return result;
  };
  const pinnedBottomRowData = useMemo(() => {
    return createData(1, 'Bottom');
  }, [rows, filteredRows]);

  const firstInfo =
    reportQuarter === 'All'
      ? 'Report Quarter: All Quarter'
      : `Report Quarter: ${reportQuarter}`;
 const secondInfo = `Manufacturer: ${selectedRows[count - 1]?.mfgId.replace(/\s+/g, ' ')} ${
    selectedRows[count - 1]?.mfgName.replace(/\s+/g, ' ')                      
  }`;
  const thirdInfo = rebateProgramNameValue;
  const fourthInfo = '';

  function divideArray(array, parts) {
    const { length } = array;
    const partSize = Math.ceil(length / parts); // Calculate the size of each part

    const dividedParts = [];
    let index = 0;

    while (index < length) {
      dividedParts.push(array.slice(index, index + partSize));
      index += partSize;
    }

    return dividedParts;
  }

  const [exportFileName, setExportFileName] = React.useState(null);
  const exportAllTpPDF = (name) => {
    setExportFileName(name);
    setApplyPagination(false);
    setExportAllClicked(true);
    const selectedMfg = selectedRows?.map((item) => item.mfgId);
    const numOfApiCallRequired = Math.ceil(selectedMfg?.length / 1000);
    const dividedArray = divideArray(selectedMfg, numOfApiCallRequired);
    setNumOfApi(numOfApiCallRequired);
    dispatch(setExportAllData([], 'noData'));
    dispatch(
      getQuarterlyPaymentForExportAll(
        {
          rebateProgramCd: rebateProgram,
          quarter:
            reportQuarter === 'All'
              ? '1Q1991'
              : reportQuarter === 'DRAMS Invoice Start Quarter'
              ? dramsInvoice
              : reportQuarter === 'Earliest Quarter for Drug Details'
              ? earliestQtr
              : '',
          thresh: useBalanceThreshold ? threshold : null,
        },
        dividedArray
      )
    );
  };

  const addPinnedRowData = (exportAllData) => {
    const exportAllDataObj = {};
    for (const key in exportAllData) {
      const data = JSON.parse(JSON.stringify(exportAllData[key]));
      const pinnedRow = {
        invoiceStatusCd: 'Total :',
        sumOfAmount: 0,
        totCurrQtrAmt: 0,
        paidAmt: 0,
        bold: true,
      };
      for (let i = 0; i < data.length; i += 1) {
        pinnedRow.sumOfAmount += data[i].sumOfAmount;
        pinnedRow.totCurrQtrAmt += data[i].totCurrQtrAmt;
        pinnedRow.paidAmt += data[i].paidAmt;
      }
      data.push(pinnedRow);
      exportAllDataObj[key] = data;
    }

    return exportAllDataObj;
  };

  React.useEffect(() => {
    if (numOfApi === Object.keys(exportAllData).length) {
      dispatch(setLoading(true));
      let allData = [];
      for (const key in exportAllData) {
        allData = [...allData, ...exportAllData[key]];
      }
      allData = allData.map((item) => {
        return {
          ...item,
        };
      });
      let groupedData = groupBy(allData, 'mfgId');
      groupedData = addPinnedRowData(groupedData);
      const header = {
        reportName: 'Quarterly Payments Report',
        fileName: exportFileName,
      };

      const mapping = {};

      for (let i = 0; i < selectedRows.length; i += 1) {
        mapping[
          selectedRows[i].mfgId
        ] = `${selectedRows[i].mfgId} - ${selectedRows[i].mfgName}`;
      }
      const options = {
        info: { thirdInfo, firstInfo },
        titleName: 'Manufacturer: ',
        mapping,
      };
      const paginationArr = Object.keys(groupedData);
      paginationArr.sort();
      const widths = [];
      if (exportAllClicked) {
        setExportAllClicked(false);
        exportAllPDF(
          header,
          groupedData,
          columnApi,
          userDefaults,
          options,
          paginationArr,
          widths,
          logInUser
        );
      }
      dispatch(setLoading(false));
    }
  }, [exportAllData]);
  const onGridReady = (params) => {
    setColumnApi(params.columnApi);
  };
  const onFilterChanged = (rowData, type) => {
    setFilteredRow(rowData);
  };

  return (
    <div className="tabs-container">
      <Spinner load={isLoading} />
      <CustomSeparator
        parent1="Summary Reports"
        parent2="Display Quarterly Payment"
      />
      <div className="tab-body">
        {!fullScreenView && (
          <>
            <div className="row">
              <div className="col-12  ">
                <div className="form-wrapper p-0 mt-2">
                  <div className="mui-custom-form input-md field-xl">
                    <div className="row mb-5">
                      <div className="col-12">
                        <div className="float-right">
                          <PaginationBox
                            array={paginationArray}
                            count={count}
                            handleClick={handleClick}
                            handleBlur={handleBlur}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-6">
                        <TextField
                          label="Manufacturer ID: "
                          id="Manufacturer ID:"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={selectedRows[count - 1]?.mfgId}
                        />
                      </div>
                      <div className="col-6">
                        <TextField
                          label="Manufacturer Name: "
                          id="Manufacturer Name"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={selectedRows[count - 1]?.mfgName?.replace(/\s+/g, ' ')}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <TextField
                          label="Rebate Program: "
                          id="Rebate Program"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={rebateProgramNameValue}
                        />
                      </div>
                      <div className="col-6">
                        <TextField
                          label="Quarter Options: "
                          id="Quarter Options"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={reportQuarter}
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-6">
                        <TextField
                          label="Total Paid: "
                          id="Total Paid"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={
                            totalAmt
                              ? `$ ${totalAmt.toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                })}`
                              : '$0.00'
                          }
                        />
                      </div>
                      <div className="col-6">
                        <div
                          className="filter-checkbox"
                          style={{ marginTop: 28 }}
                        >
                          <FormControlLabel
                            control={
                              <>
                                <div style={{ width: 275, marginLeft: 5 }}>
                                  Use Report Balance Due Threshold:
                                </div>
                                <Checkbox
                                  color="primary"
                                  disabled
                                  checked={useBalanceThreshold}
                                />
                              </>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="row" style={{ marginTop: 15 }}>
          <div
            className="unallocated bottom-pinned-bold"
            style={{ width: '100vw' }}
          >
            <TableGridWithFeatures
              ref={gridRef}
              gridHeight={viewPortHeight_355}
              gridCall={onGridReady}
              columnDefs={TableHeaderForDetails}
              rowData={rows?.map((item) => {
                return {
                  ...item,
                  sumOfAmount: item.sumOfAmount ? item.sumOfAmount : '0.00',
                  totCurrQtrAmt: item.totCurrQtrAmt
                    ? item.totCurrQtrAmt
                    : '0.00',
                  paidAmt: item.paidAmt ? item.paidAmt : '0.00',
                };
              })}
              data-testid="onEditingStoppedd"
              enterMovesDown
              rowSelection="multiple"
              // showResetFilter={false}
              countValue={count}
              defaultCol={{
                wrapHeaderText: true,
                autoHeaderHeight: true,
              }}
              frameworkComponents={{
                CheckBoxSelectedForRCellRender: (cellInfo) => {
                  if (cellInfo.data.isBottomRow) {
                    return null;
                  }
                  return (
                    <div
                      style={{ marginTop: 10 }}
                      className="ag-cell ag-cell-not-inline-editing ag-cell-normal-height ag-cell-value ag-cell-focus"
                    >
                      <div className="tbl-checkbox frr-checkbox-no-label">
                        <FormControlLabel
                          data-testid="checkBox"
                          label="checkbox"
                          control={<Checkbox color="primary" />}
                          disabled={parseInt(cellInfo.value) !== 1}
                          checked={parseInt(cellInfo.value) === 1}
                          // value={parseInt(cellInfo.value, [0, 1]) === 1}
                        />
                      </div>
                    </div>
                  );
                },
                FullNameRender: (cellInfo) => {
                  const name = cellInfo?.value;
                  if (name && name.length > 22) {
                    return (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="labeler-tooltip">{name}</Tooltip>}
                      >
                        <div>{name}</div>
                      </OverlayTrigger>
                    );
                  }
                  return <div>{name}</div>;
                },
              }}
              exportAllTpPDF={(name) => {
                setApplyPagination(false);
                exportAllTpPDF(name);
              }}
              hideTotalCount={false}
              exportFile
              isScrollOn
              fileName="QuarterlySummary"
              reportName="Quarterly Payment Report"
              fullScreenView={fullScreenView}
              handleFullScreenViewClick={changeFullScreenView}
              info={{ firstInfo, secondInfo, thirdInfo, fourthInfo }}
              pinnedBottomRowData={pinnedBottomRowData}
              onFilterData={onFilterChanged}
              getFilterApply={getFilterApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayQuarterlyPayments;
